import React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

import UnityHandler from '../../designer/UnityHandler';
import { DatabaseContext } from '../database/DatabaseHandler';
import { ParametersContext } from '../parametersProcessor/ParametersProcessor';
import { CartContext } from '../cart/CartHandler';
import PrestaGlobals from '../common/PrestaGlobals';
import Fetcher from '../common/Fetcher';
import ScriptsEditor, { ILoadedScripts } from './ScriptsEditor';
import ScriptDiffDialog from './ScriptDiffDialog';
import CreateProductForm from './CreateProductForm';
import AdminApi, { IScriptRef, IProductRef } from './AdminApi';

/**
 * Szkriptek tab (admin only): the product's UCon script and the style script sent to Unity, side by
 * side, loadable from files (ScriptsEditor).
 *
 * UCon (EA-439/440/441/443):
 *  - live preview (Alkalmaz) via updateScripts — re-sends UCon then style script (3D rebuilds),
 *  - persist: Update (overwrite the assigned script, after a diff against the stored one) / Save as
 *    new (insert + assign),
 *  - shows the assigned script id+name and which products use it.
 * Style: the EXACT script sent to Unity (SetStyleScript), as ParametersProcessor.generateStyleScript()
 * makes it. "Alkalmaz" sets the edit as the cart's style override (CartHandler.designStyle): UnityView
 * sends it instead of the generated one until cleared, and an Árajánlat saves it with the design.
 * Product-level changes go through the Paraméterek tab.
 */
const ScriptsTab: React.FC = () => {
    const data = React.useContext(DatabaseContext);
    const cart = React.useContext(CartContext);
    const paramContext = React.useContext(ParametersContext);
    const [info, setInfo] = React.useState<IScriptRef | null>(null);
    const [products, setProducts] = React.useState<IProductRef[]>([]);
    const [status, setStatus] = React.useState<string | null>(null);
    // "Mentés (felülírás)" waiting on its diff: the text to save, and the stored script (null while it loads)
    const [overwrite, setOverwrite] = React.useState<{ text: string; saved: string | null } | null>(null);

    // Regenerate whenever the parameters change — the same trigger UnityView uses to re-send it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const generated = React.useMemo(() => paramContext.generateStyleScript(), [paramContext.parameters]);
    const styleScript = cart.designStyle ?? generated;

    const loadInfo = React.useCallback(async () => {
        const res = await AdminApi.getScriptInfo(PrestaGlobals.productId);
        if (res && 'id' in res) {
            setInfo(res);
            const used = await AdminApi.productsUsingScript(res.id);
            setProducts(used ?? []);
        } else {
            setInfo(null);
            setProducts([]);
        }
    }, []);

    React.useEffect(() => { loadInfo(); }, [loadInfo]);

    const sendUcon = (parsed: any) => {
        cart.setAdminDesign(true);
        data.updateScripts([...(data.paramScripts ?? [])], parsed);
    };

    const styleError = (parsed: any): string | null => {
        if (!Array.isArray(parsed)) return 'A stílus szkriptnek JSON tömbnek kell lennie ([ ... ]).';
        if (!UnityHandler.instance) return 'A Unity designer még nem töltődött be.';
        return null;
    };

    // UnityView sends it (and notifies the cart). The parameters' own script needs no override, so
    // a restored or loaded copy of it leaves the parameters in charge.
    const sendStyle = (parsed: any[]) => {
        if (JSON.stringify(parsed) === JSON.stringify(generated)) {
            cart.setDesignStyle(null);
            return false;
        }
        cart.setDesignStyle(parsed);
        cart.setAdminDesign(true);
        return true;
    };

    const handleStyle = (parsed: any) => {
        const error = styleError(parsed);
        if (error) {
            setStatus(error);
            return;
        }
        setStatus(sendStyle(parsed)
            ? `Stílus elküldve a Unity-nak (${parsed.length} elem) — felülbírálás aktív, a paraméterek nem írják felül.`
            : 'A stílus azonos a paraméterekből generálttal — nincs felülbírálás.');
    };

    // Both in one render: UnityView sends the UCon script, then the style script.
    const handleLoad = ({ ucon, style }: ILoadedScripts) => {
        const sent: string[] = [];
        const error = style ? styleError(style) : null;
        if (style && !error) {
            sent.push(sendStyle(style) ? `stílus (${style.length} elem, felülbírálásként)` : 'stílus (a paraméterekből generált)');
        }
        if (ucon) {
            sendUcon(ucon);
            sent.push('UCon (élő előnézet, nincs mentve)');
        }
        setStatus([
            sent.length ? `Alkalmazva: ${sent.join(' + ')}.` : '',
            error ? `A stílus nincs alkalmazva: ${error}` : '',
        ].filter(Boolean).join(' '));
    };

    const handleReset = () => {
        cart.setDesignStyle(null);
        setStatus('Felülbírálás törölve — a paraméterekből generált szkript él.');
    };

    // Back to what the database holds: the product's UCon script and parameters, no style override.
    const handleRestoreProduct = () => {
        cart.setDesignStyle(null);
        cart.setAdminDesign(false);
        data.reloadScripts();
    };

    // The stored script is what /scripts serves: the live preview has replaced data.uconScript.
    const handleUpdate = async (text: string) => {
        if (!info) { setStatus('Nincs hozzárendelt szkript ehhez a termékhez.'); return; }
        setOverwrite({ text, saved: null });
        const stored = new Fetcher(`${PrestaGlobals.virtualUri}/scripts?id_product=${PrestaGlobals.productId}`);
        await stored.request();
        setOverwrite((pending) => pending && pending.text === text
            ? { text, saved: stored.Result == null ? '' : JSON.stringify(stored.Result) }
            : pending);
    };

    const confirmUpdate = async () => {
        if (!info || !overwrite) return;
        const { text } = overwrite;
        setOverwrite(null);
        const res = await AdminApi.updateScript(info.id, text);
        setStatus(res?.success ? `Mentve (felülírva): #${info.id} ${info.name}` : 'Mentés sikertelen.');
    };

    const handleSaveAsNew = async (text: string) => {
        const name = window.prompt('Új szkript neve:', (info?.name ? `${info.name} (másolat)` : 'Új szkript'));
        if (!name) return;
        const res = await AdminApi.saveScriptAsNew(name, text, PrestaGlobals.productId, true);
        if (res?.success) {
            setStatus(`Mentve újként: #${res.id_configuration_script} „${name}”${res.assigned ? ' (hozzárendelve)' : ''}`);
            await loadInfo();
        } else {
            setStatus('Mentés újként sikertelen.');
        }
    };

    const uconHeader = (
        <>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                {info
                    ? <Chip size="small" label={`Szkript #${info.id}: ${info.name}`} />
                    : <Chip size="small" color="warning" label="Nincs hozzárendelt szkript" />}
                {info && (
                    <Typography variant="caption" color="text.secondary">
                        Használja: {products.length} termék
                        {products.length > 0 && ` (${products.map(p => `#${p.id_product}${p.name ? ' ' + p.name : ''}`).join(', ')})`}
                    </Typography>
                )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Az „Alkalmaz” élő előnézet (UCon majd stílus szkript újraküldése). A „Mentés” az adatbázisba ír.
                A kártyákkal vezérelt mezőket (pl. polcszám) a stílus-szkript felülírja.
            </Typography>
        </>
    );

    const styleHeader = (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            A Unity-nak leküldött stílus szkript (SetStyleScript). Az „Alkalmaz” felülbírálásként küldi a
            designernek: törlésig a paraméter-változások nem írják felül, és az Árajánlat fül a tervvel együtt
            menti. A termék tartós módosításához használja a Paraméterek fület.
        </Typography>
    );

    return (
        <ScriptsEditor
            status={status}
            onLoad={handleLoad}
            pairFileName={`termek-${PrestaGlobals.productId}.scripts.json`}
            onRestoreProduct={handleRestoreProduct}
            ucon={{
                value: data.uconScript,
                applyLabel: 'Alkalmaz (élő előnézet)',
                dirtyNote: 'Nem mentett változás — élő előnézet; mentéshez használja a Mentés gombokat.',
                onApply: sendUcon,
                fileName: `termek-${PrestaGlobals.productId}.ucon.json`,
                header: uconHeader,
                renderExtraActions: (text, valid) => (
                    <>
                        <Button variant="outlined" size="small" color="primary" disabled={!valid || !info}
                            onClick={() => handleUpdate(text)}>
                            Mentés (felülírás)
                        </Button>
                        <Button variant="outlined" size="small" disabled={!valid}
                            onClick={() => handleSaveAsNew(text)}>
                            Mentés újként
                        </Button>
                    </>
                ),
            }}
            style={{
                value: styleScript,
                applyLabel: 'Alkalmaz (Unity-nak küld)',
                dirtyNote: 'Nem alkalmazott változás.',
                onApply: handleStyle,
                fileName: `termek-${PrestaGlobals.productId}.style.json`,
                header: styleHeader,
                renderExtraActions: () => cart.designStyle != null && (
                    <Button variant="outlined" size="small" color="warning" onClick={handleReset}>
                        Felülbírálás törlése
                    </Button>
                ),
            }}
        >
            <Box sx={{ mt: 2 }}>
                <CreateProductForm />
            </Box>
            <ScriptDiffDialog
                open={!!overwrite}
                title={info ? `Felülírás: #${info.id} ${info.name}` : 'Felülírás'}
                saved={overwrite?.saved ?? null}
                next={overwrite?.text ?? ''}
                sharedBy={products.length}
                onCancel={() => setOverwrite(null)}
                onConfirm={confirmUpdate}
            />
        </ScriptsEditor>
    );
};

export default ScriptsTab;
